import * as React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import type {
    SideMenuExpandClickHandler,
    SideMenuLinkClickHandler,
} from '../../../common/components/molecules/SideMenu/SideMenu.types';
import type { MenuKey, MenuNode } from '../../../common/hooks/useMenuState';
import type { EcommerceSideMenuProps } from './EcommerceSideMenu.types';

import { useAsyncDispatcher } from '../../../common/hooks/useAsyncDispatcher';
import useMenuState from '../../../common/hooks/useMenuState';
import { safeSplit } from '../../../common/utils/stringUtils';
import { contentDeliveryApi } from '../../../episerver/api/contentDeliveryApi';
import useReactRouting from '../../hooks/useReactRouting';

const useEcommerceMenuState = (menuRootNode: MenuNode | undefined) => {
    const { pathname } = useLocation();
    const {
        expandedNodesTrail,
        activeNodesTrail,
        setExpandedNodesTrailByUrlSegments,
        setActiveNodesTrailByUrlSegments,
        toggleExpandedNode,
    } = useMenuState(menuRootNode);

    const activeIdRef = React.useRef<MenuKey>(null);

    useEffect(() => {
        if (menuRootNode) {
            const urlSegments = safeSplit(pathname, '/', { removeEmptyEntries: true });
            setExpandedNodesTrailByUrlSegments(urlSegments);
            setActiveNodesTrailByUrlSegments(urlSegments);
        }
    }, [
        pathname,
        menuRootNode,
        setExpandedNodesTrailByUrlSegments,
        setActiveNodesTrailByUrlSegments,
    ]);

    useEffect(() => {
        activeIdRef.current = activeNodesTrail[activeNodesTrail.length - 1];
    }, [activeNodesTrail]);

    return {
        activeNodesTrail,
        expandedNodesTrail,
        toggleExpandedNode,
        activeIdRef,
    };
};

export const useEcommerceSideMenu = (props: EcommerceSideMenuProps) => {
    const { execute, result: menu } = useAsyncDispatcher((_ecommercePageContentId: number) =>
        contentDeliveryApi.getMenu(_ecommercePageContentId.toString(), 3),
    );

    const { expandedNodesTrail, activeNodesTrail, toggleExpandedNode, activeIdRef } =
        useEcommerceMenuState(menu);

    const { push } = useReactRouting();

    useEffect(() => {
        if (props.ecommercePageContentId) {
            execute(props.ecommercePageContentId);
        }
    }, [execute, props.ecommercePageContentId]);

    const onLinkClick: SideMenuLinkClickHandler = ({ e, url, id, hasChildren, type, name }) => {
        if (type !== 'Shortcut') {
            e.preventDefault();
            e.stopPropagation();
            if (activeIdRef.current !== id) {
                push(url);
            }
        }
        if (!hasChildren) {
            props.onLastLevelLinkClick?.(e, name);
        } else {
            props.onLinkClick?.(e, name);
        }
    };

    const onExpandClick: SideMenuExpandClickHandler = ({ e, id, parentId }) => {
        e.preventDefault();
        e.stopPropagation();
        toggleExpandedNode(id, parentId);
    };

    return {
        menu,
        activeNodesTrail,
        expandedNodesTrail,
        onExpandClick,
        onLinkClick,
    };
};
